import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, RefreshCw, Wallet, PiggyBank, Target, ArrowUpRight, ArrowDownRight, Minus, Pencil, Check, X, Loader2 } from "lucide-react";

const chf = (v) => `${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CHF`;
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const monthLabel = (m, short = false) => { if (!m) return ""; const name = MONTHS[parseInt(m.slice(5, 7), 10) - 1] || m; return short ? `${name.slice(0, 3)} ${m.slice(2, 4)}` : `${name} ${m.slice(0, 4)}`; };
const shift = (m, d) => { const idx = parseInt(m.slice(0, 4), 10) * 12 + parseInt(m.slice(5, 7), 10) - 1 + d; return `${String(Math.floor(idx / 12)).padStart(4, "0")}-${String((idx % 12) + 1).padStart(2, "0")}`; };
const STATUS_BAR = { over: "bg-rose-500", warn: "bg-amber-400", ok: "bg-emerald-500", none: "bg-slate-300" };

function Delta({ delta, pct, invert = false, testid }) {
  if (!delta) return <span className="inline-flex items-center gap-0.5 text-xs text-slate-400" data-testid={testid}><Minus className="h-3 w-3" /> wie Vormonat</span>;
  const up = delta > 0;
  const bad = invert ? !up : up;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${bad ? "text-rose-600" : "text-emerald-600"}`} data-testid={testid}>
      {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {up ? "+" : "−"}{chf(Math.abs(delta))}{pct != null ? ` (${pct > 0 ? "+" : ""}${pct} %)` : ""} vs. Vormonat
    </span>
  );
}

function HeroTile({ title, value, icon: Icon, tone, children, testid }) {
  const tones = { rose: "border-rose-200 from-rose-50 text-rose-700 bg-rose-500", emerald: "border-emerald-200 from-emerald-50 text-emerald-700 bg-emerald-500", sky: "border-sky-200 from-sky-50 text-sky-700 bg-sky-500" };
  const [border, from, text, bg] = tones[tone].split(" ");
  return (
    <Card className={`p-5 border ${border} bg-gradient-to-br ${from} via-white to-white`} data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${text}`}><Icon className="h-3.5 w-3.5" />{title}</div>
          <div className="mt-2 text-3xl font-bold text-slate-900 truncate" data-testid={`${testid}-value`}>{value}</div>
          <div className="mt-3 text-xs text-slate-600 space-y-1">{children}</div>
        </div>
        <div className={`h-10 w-10 rounded-xl ${bg} text-white flex items-center justify-center flex-shrink-0 shadow-sm`}><Icon className="h-5 w-5" /></div>
      </div>
    </Card>
  );
}

function TrendBars({ series, month }) {
  const max = Math.max(1, ...series.flatMap((s) => [s.spent, s.income]));
  return (
    <Card className="p-5" data-testid="private-trend">
      <div className="flex items-center justify-between mb-3"><h3 className="text-sm font-semibold text-slate-900">Verlauf 6 Monate</h3>
        <div className="flex items-center gap-3 text-[11px] text-slate-500"><span className="inline-flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-emerald-500 inline-block" />Einkommen</span><span className="inline-flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-rose-400 inline-block" />Ausgaben</span></div></div>
      <div className="grid grid-cols-6 gap-2 items-end h-28">
        {series.map((s) => (
          <div key={s.month} className="flex flex-col items-center gap-1 h-full justify-end" title={`${monthLabel(s.month)}: Einkommen ${chf(s.income)} · Ausgaben ${chf(s.spent)}`} data-testid={`private-trend-${s.month}`}>
            <div className="flex items-end gap-0.5 w-full justify-center h-full">
              <div className="w-3 rounded-t bg-emerald-500 transition-[height] duration-500" style={{ height: `${Math.max(2, (s.income / max) * 100)}%` }} />
              <div className="w-3 rounded-t bg-rose-400 transition-[height] duration-500" style={{ height: `${Math.max(2, (s.spent / max) * 100)}%` }} />
            </div>
            <span className={`text-[10px] ${s.month === month ? "font-semibold text-slate-900" : "text-slate-400"}`}>{monthLabel(s.month, true)}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function CategoryRow({ c, editing, draft, onDraft }) {
  const pct = c.budget ? Math.min(100, (c.spent / c.budget) * 100) : c.spent > 0 ? 100 : 0;
  return (
    <div className="py-3 border-b border-slate-100 last:border-0" data-testid={`private-cat-${c.code}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex items-center gap-2">
          <span className="font-mono text-[11px] text-slate-400">{c.code}</span>
          <span className="font-medium text-slate-900 truncate">{c.name}</span>
          {c.status === "over" && <Badge className="bg-rose-100 text-rose-800 border border-rose-200 text-[10px]" data-testid={`private-cat-over-${c.code}`}>Budget überschritten</Badge>}
          {c.status === "warn" && <Badge className="bg-amber-100 text-amber-900 border border-amber-200 text-[10px]">über 80 %</Badge>}
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="text-right"><div className="text-[10px] uppercase tracking-wide text-slate-400">Ausgaben</div><div className="font-semibold text-slate-900" data-testid={`private-cat-spent-${c.code}`}>{chf(c.spent)}</div></div>
          <div className="text-right w-28"><div className="text-[10px] uppercase tracking-wide text-slate-400">Budget</div>
            {editing ? <Input type="number" step="10" min="0" value={draft ?? ""} onChange={(e) => onDraft(c.code, e.target.value)} placeholder="–" className="h-7 text-right text-sm" data-testid={`private-budget-input-${c.code}`} />
              : <div className="font-medium text-slate-700" data-testid={`private-cat-budget-${c.code}`}>{c.budget ? chf(c.budget) : <span className="text-slate-400">kein Budget</span>}</div>}
          </div>
          <div className="text-right w-24"><div className="text-[10px] uppercase tracking-wide text-slate-400">Vormonat</div><div className="text-slate-600" data-testid={`private-cat-prev-${c.code}`}>{chf(c.prev_spent)}</div></div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${STATUS_BAR[c.status]} transition-[width] duration-500`} style={{ width: `${pct}%` }} /></div>
        <span className="text-[11px] text-slate-500 w-44 text-right whitespace-nowrap">{c.budget ? `${c.pct_of_budget} % · Rest ${chf(c.remaining)}` : ""}</span>
        <Delta delta={c.delta} pct={c.delta_pct} invert={false} testid={`private-cat-delta-${c.code}`} />
      </div>
    </div>
  );
}

export default function PrivateBudgetDashboard({ company }) {
  const companyId = company?._id || company?.id;
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!companyId) return;
    if (!silent) setLoading(true);
    try { const r = await apiClient.get(`/companies/${companyId}/private-budget?month=${month}`); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Budget konnte nicht geladen werden."); }
    finally { setLoading(false); }
  }, [companyId, month]);
  useEffect(() => { load(); }, [load]);

  const startEdit = () => { setDrafts(Object.fromEntries((data?.categories || []).map((c) => [c.code, c.budget ? String(c.budget) : ""]))); setEditing(true); };
  const save = async () => {
    setSaving(true);
    try {
      const budgets = Object.fromEntries(Object.entries(drafts).map(([k, v]) => [k, v === "" ? null : parseFloat(v)]));
      await apiClient.put(`/companies/${companyId}/private-budget`, { budgets });
      toast.success("Budgets gespeichert"); setEditing(false); load(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };
  const visible = useMemo(() => (data?.categories || []).filter((c) => editing || c.budget > 0 || c.spent > 0 || c.prev_spent > 0), [data, editing]);
  const hidden = (data?.categories?.length || 0) - visible.length;
  const isCurrent = month === new Date().toISOString().slice(0, 7);

  return (
    <div className="space-y-6" data-testid="private-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Haushaltsbudget</h1>
          <p className="mt-1 text-sm text-slate-500">{company?.name} · Privat-Buchhaltung · Ausgaben pro Kategorie im Vergleich zum Vormonat</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setMonth(shift(month, -1))} data-testid="private-month-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-[150px] text-center font-medium text-slate-900" data-testid="private-month-label">{monthLabel(month)}</span>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setMonth(shift(month, 1))} disabled={isCurrent} data-testid="private-month-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => load(true)} disabled={loading} data-testid="private-refresh"><RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />Aktualisieren</Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3" data-testid="private-hero-strip">
        <HeroTile title={`Ausgaben ${monthLabel(month, true)}`} value={chf(data?.total_spent)} icon={Wallet} tone="rose" testid="private-hero-spent">
          <Delta delta={data?.delta_total} pct={data?.total_prev_spent ? Math.round((data.delta_total / data.total_prev_spent) * 1000) / 10 : null} testid="private-hero-delta" />
          <div>Vormonat: {chf(data?.total_prev_spent)}</div>
        </HeroTile>
        <HeroTile title="Einkommen" value={chf(data?.income)} icon={PiggyBank} tone="emerald" testid="private-hero-income">
          <div className={(data?.saved || 0) < 0 ? "text-rose-600 font-medium" : "text-emerald-700 font-medium"} data-testid="private-hero-saved">{(data?.saved || 0) < 0 ? "Mehr ausgegeben als eingenommen: " : "Übrig nach Ausgaben: "}{chf(Math.abs(data?.saved || 0))}</div>
          <div>Vormonat: {chf(data?.prev_income)}</div>
        </HeroTile>
        <HeroTile title="Monatsbudget" value={data?.total_budget ? chf(data.total_budget) : "—"} icon={Target} tone="sky" testid="private-hero-budget">
          {data?.total_budget ? (<>
            <div className={(data.remaining || 0) < 0 ? "text-rose-600 font-medium" : "text-slate-700"} data-testid="private-hero-remaining">{(data.remaining || 0) < 0 ? `Überzogen um ${chf(-data.remaining)}` : `Noch verfügbar: ${chf(data.remaining)}`}</div>
            {data.over_count > 0 && <div className="text-rose-600">{data.over_count} Kategorie{data.over_count === 1 ? "" : "n"} über Budget</div>}
          </>) : <div>Noch kein Budget gesetzt – unten «Budgets festlegen».</div>}
        </HeroTile>
      </div>

      {data?.series && <TrendBars series={data.series} month={month} />}

      <Card className="p-5" data-testid="private-categories">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div><h3 className="text-sm font-semibold text-slate-900">Kategorien</h3><p className="text-xs text-slate-500">Budget pro Aufwandskonto des Privat-Kontenplans · Ist-Werte aus den gebuchten Belegen</p></div>
          {editing ? (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(false)} data-testid="private-budget-cancel"><X className="h-4 w-4 mr-1" />Abbrechen</Button>
              <Button size="sm" onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="private-budget-save">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}Budgets speichern</Button>
            </div>
          ) : <Button variant="outline" size="sm" onClick={startEdit} disabled={!data} data-testid="private-budget-edit"><Pencil className="h-4 w-4 mr-1" />{data?.total_budget ? "Budgets anpassen" : "Budgets festlegen"}</Button>}
        </div>
        {loading && !data ? <div className="py-10 text-center text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Lade Budget …</div>
          : visible.length === 0 ? <div className="py-10 text-center text-sm text-slate-500" data-testid="private-categories-empty">Noch keine Ausgaben in diesem Monat. Belege per Posteingang oder WhatsApp erfassen – sie landen automatisch in der passenden Kategorie.</div>
          : visible.map((c) => <CategoryRow key={c.code} c={c} editing={editing} draft={drafts[c.code]} onDraft={(code, v) => setDrafts((d) => ({ ...d, [code]: v }))} />)}
        {!editing && hidden > 0 && <p className="mt-3 text-[11px] text-slate-400" data-testid="private-categories-hidden">{hidden} Kategorie{hidden === 1 ? "" : "n"} ohne Budget und ohne Ausgaben ausgeblendet – über «Budgets festlegen» sichtbar.</p>}
      </Card>
    </div>
  );
}
